
import { Progress } from "@/components/ui/progress";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useEvaluationUsage } from "@/hooks/useEvaluationUsage";
import { AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export function UsageStats() {
  const { usage, isLoading, error } = useEvaluationUsage();

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Evaluation Usage</CardTitle>
      </CardHeader>
      <CardContent>
        {error ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Failed to load evaluation usage: {error}
            </AlertDescription>
          </Alert>
        ) : isLoading ? (
          <div className="space-y-3">
            <div className="h-4 bg-muted animate-pulse rounded" />
            <div className="h-2 bg-muted animate-pulse rounded" />
            <div className="h-4 bg-muted animate-pulse rounded" />
          </div>
        ) : usage ? (
          <>
            <div className="mb-6">
              <p className="text-sm text-muted-foreground mb-2">Evaluations Used</p>
              <p className="text-xl font-semibold">{usage.evaluations_used}</p>
            </div>
            
            <Progress 
              value={usage.usage_percentage} 
              className="h-2" 
            />
            <div className="mt-2 text-sm space-y-1">
              <p className="text-muted-foreground">
                {usage.evaluations_used} / {usage.max_evaluations} evaluations used
              </p>
              <p className="text-muted-foreground">
                {usage.max_evaluations - usage.evaluations_used} evaluations remaining
              </p>
            </div>

            <Separator className="my-4" />
            
            <div className="space-y-1">
              <h4 className="text-sm font-medium">Usage History</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-muted-foreground">Last Updated:</p>
                  <p>{formatDate(usage.last_updated)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Last Purchased:</p>
                  <p>{usage.last_purchased ? formatDate(usage.last_purchased) : "Never purchased"}</p>
                </div>
              </div>
            </div>

            {usage.usage_percentage >= 90 && (
              <Alert variant="destructive" className="mt-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {usage.can_create_tests 
                    ? "You're approaching your evaluation limit. Tests will be automatically paused when the limit is reached."
                    : "You've reached your evaluation limit. All active tests have been paused."
                  }
                </AlertDescription>
              </Alert>
            )}
          </>
        ) : (
          <p className="text-muted-foreground">No usage data available</p>
        )}
      </CardContent>
    </Card>
  );
}
